import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Mail, Send, X } from 'lucide-react';
import { api } from '../../api';
import type { MailMessage, MailStatus } from '../../app/legacy-types';
import { formatContactDateTime } from './contactCenterModel';

export type SentMailResult = {
  success: boolean;
  storedInSent: boolean;
  storedMailMessageId?: string | null;
  contactMessageId?: string | null;
  warning?: string | null;
};

export type NewEmailComposerProps = {
  initialTo?: string;
  initialSubject?: string;
  replyMessageId?: string;
  onClose: () => void;
  onSent: (result: SentMailResult) => void | Promise<void>;
};

export function NewEmailComposer(props: NewEmailComposerProps) {
  const [to, setTo] = useState(props.initialTo || '');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState(props.initialSubject || '');
  const [body, setBody] = useState('');
  const [showCopies, setShowCopies] = useState(false);
  const initializedReply = useRef(false);
  const { data: status, isLoading: statusLoading } = useQuery({ queryKey: ['mail-status'], queryFn: () => api<MailStatus>('/mail/status') });
  const { data: replyMessage, isLoading: replyLoading } = useQuery({
    queryKey: ['mail-reply-source', props.replyMessageId || ''],
    queryFn: () => api<MailMessage>(`/mail/messages/${props.replyMessageId}`),
    enabled: Boolean(props.replyMessageId),
  });

  useEffect(() => {
    if (!replyMessage || initializedReply.current) return;
    initializedReply.current = true;
    setTo(replyMessage.from_address || '');
    setCc((replyMessage.cc_addresses || []).map(item => item.address).filter(Boolean).join(', '));
    setShowCopies(Boolean(replyMessage.cc_addresses?.length));
    setSubject(/^re\s*:/i.test(replyMessage.subject) ? replyMessage.subject : `Re: ${replyMessage.subject}`);
  }, [replyMessage]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const close = (event: KeyboardEvent) => { if (event.key === 'Escape') props.onClose(); };
    window.addEventListener('keydown', close);
    return () => { document.body.style.overflow = previousOverflow; window.removeEventListener('keydown', close); };
  }, [props.onClose]);

  const send = useMutation({
    mutationFn: () => api<SentMailResult>('/mail/send', {
      method: 'POST',
      body: JSON.stringify({
        to,
        cc,
        bcc,
        subject,
        body,
        replyMessageId: replyMessage?.id || undefined,
      }),
    }),
    onSuccess: result => { void props.onSent(result); },
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!to.trim() || !subject.trim() || !body.trim() || send.isPending) return;
    send.mutate();
  }

  return <div className="modal-backdrop contact-email-compose-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget && !send.isPending) props.onClose(); }}>
    <form className="modal-card contact-email-compose" role="dialog" aria-modal="true" aria-labelledby="contact-email-compose-title" onSubmit={submit} onMouseDown={event => event.stopPropagation()}>
      <header><span><Mail /></span><div><small>КОНТАКТ-ЦЕНТР · EMAIL</small><h2 id="contact-email-compose-title">{replyMessage ? 'Ответ на письмо' : 'Новое письмо'}</h2><p>{replyMessage ? `Ответ в цепочке «${replyMessage.subject}»` : 'Отправка через настроенный SMTP портала'}</p></div><button type="button" className="icon-button" onClick={props.onClose} disabled={send.isPending} aria-label="Закрыть"><X /></button></header>
      {(statusLoading || (props.replyMessageId && replyLoading)) && <div className="page-skeleton"><span /><span /><span /></div>}
      {!statusLoading && !status?.smtpEnabled && <div className="form-error">Исходящая почта не настроена. Настройте SMTP в администрировании.</div>}
      {!statusLoading && status?.smtpEnabled && <div className="contact-email-compose-fields">
        <label><span>От</span><input value={status.username || status.fromEmail || 'Intez Helpdesk'} disabled /></label>
        <label><span>Кому</span><input value={to} onChange={event => setTo(event.target.value)} type="text" required placeholder="client@example.ru" autoFocus /></label>
        <button type="button" className="text-button" onClick={() => setShowCopies(value => !value)}>{showCopies ? 'Скрыть копии' : 'Копия / скрытая копия'}</button>
        {showCopies && <><label><span>Копия</span><input value={cc} onChange={event => setCc(event.target.value)} placeholder="Несколько адресов через запятую" /></label><label><span>Скрытая</span><input value={bcc} onChange={event => setBcc(event.target.value)} placeholder="Необязательно" /></label></>}
        <label><span>Тема</span><input value={subject} onChange={event => setSubject(event.target.value)} required maxLength={500} placeholder="Тема письма" /></label>
        <label className="contact-email-compose-body"><span>Сообщение</span><textarea value={body} onChange={event => setBody(event.target.value)} rows={12} required placeholder="Введите текст письма…" /></label>
        {replyMessage && <div className="contact-email-reply-context"><b>Исходное письмо</b><small>{replyMessage.from_name || replyMessage.from_address} · {formatContactDateTime(replyMessage.sent_at || replyMessage.received_at)}</small><p>{replyMessage.text_body || replyMessage.snippet}</p></div>}
      </div>}
      {send.error && <div className="form-error">{send.error instanceof Error ? send.error.message : 'Не удалось отправить письмо'}</div>}
      <footer><button type="button" className="button ghost" onClick={props.onClose} disabled={send.isPending}>Отмена</button><button className="button primary" disabled={!status?.smtpEnabled || !to.trim() || !subject.trim() || !body.trim() || send.isPending}><Send /> {send.isPending ? 'Отправка…' : 'Отправить письмо'}</button></footer>
    </form>
  </div>;
}
